import { useEffect, useState } from "react";

import type { File, Order } from "@zhihaoscm/entity";
import { ORDER_TYPE } from "@zhihaoscm/meta";
import { formatCurrency } from "@zhihaoscm/utils";

import { fileService } from "@/api/services/fileService";
import { areaLib } from "@/utils/areaLib";
import { getEnterpriseName } from "@/utils/customer";
import { fmtMoney } from "@/utils/money";
import { getPublicFileUrl } from "@/utils";

export interface PreviewPhoneProps {
    open: boolean;
    imageUrl?: string;
    title?: string;
    order?: Order;
    mainFile?: File;
    onClose: () => void;
}

type PreviewTab = "video" | "image";

/** 订单交易快照：按旧 Vue 的手机壳、媒体切换和商品信息结构展示快照。 */
export default function PreviewPhone({ open, imageUrl, order, mainFile, onClose }: PreviewPhoneProps) {
    const [tab, setTab] = useState<PreviewTab>("video");
    const [videoUrl, setVideoUrl] = useState("");
    const [imageUrls, setImageUrls] = useState<string[]>([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        if (!open) return;

        // 每次打开都回到旧版默认的视频页，避免上次切换图片的状态污染本次快照。
        setTab("video");
        setCurrentImageIndex(0);
        setVideoUrl("");
        setImageUrls(imageUrl ? [imageUrl] : mainFile?.path ? [mainFile.path] : []);

        const fileIds = [order?.productInfo?.videoId, order?.productInfo?.imgMainId, ...(order?.productInfo?.imgIds || [])]
            .filter((id): id is number => Boolean(id));
        if (!fileIds.length) return;

        let disposed = false;
        void fileService
            .getFileByIds([...new Set(fileIds)])
            .then(files => {
                if (disposed) return;
                const video = files.find(file => file.id === order?.productInfo?.videoId);
                const images = files
                    .filter(file => file.id === order?.productInfo?.imgMainId || order?.productInfo?.imgIds?.includes(file.id))
                    .map(file => file.path)
                    .filter(Boolean);
                setVideoUrl(video?.path || "");
                setImageUrls(images.length ? images : imageUrl ? [imageUrl] : mainFile?.path ? [mainFile.path] : []);
            })
            .catch(() => {
                // 文件接口失败时保留订单详情已返回的主图，保证快照仍可查看。
            });

        return () => {
            disposed = true;
        };
    }, [imageUrl, mainFile?.path, open, order]);

    if (!open) return null;

    const product = order?.productInfo;
    const currentImage = imageUrls[currentImageIndex] || imageUrls[0];
    const isImageTab = tab === "image";
    const orderType = Number(order?.orderType);
    const isGroupBuying = orderType === ORDER_TYPE.GROUP_BUYING_PRODUCTS.code;
    const deliveryAddress = product
        ? `${areaLib.getFullAreaName(product.provinceCode, product.cityCode, product.regionCode)}${product.address || ""}`
        : "--";
    const productRuleTitle =
        orderType === ORDER_TYPE.CONSIGNMENT_PRODUCTS.code
            ? "寄售规则"
            : orderType === ORDER_TYPE.GROUP_BUYING_PRODUCTS.code
              ? "团购规则"
              : "捡漏规则";

    return (
        <div
            aria-label="查看交易快照"
            className="trade-order-snapshot-mask"
            data-testid="trade-order-snapshot-dialog"
            role="dialog"
            aria-modal="true"
            onClick={onClose}
        >
            <div
                className="trade-order-snapshot-phone"
                data-testid="trade-order-snapshot-phone"
                onClick={event => event.stopPropagation()}
            >
                <div className="trade-order-snapshot-phone__page">
                    <div className="trade-order-snapshot-phone__media">
                        {isImageTab ? (
                            currentImage ? (
                                <img src={currentImage} alt="商品图片" />
                            ) : (
                                <div className="trade-order-snapshot-phone__media-empty">暂无图片</div>
                            )
                        ) : videoUrl ? (
                            <video controls height={391} preload="auto" src={videoUrl} width={391} />
                        ) : currentImage ? (
                            <img src={currentImage} alt="商品图片" />
                        ) : (
                            <div className="trade-order-snapshot-phone__media-empty">暂无预览文件</div>
                        )}
                        <div className="trade-order-snapshot-phone__tabs">
                            <button
                                className={tab === "video" ? "is-active" : ""}
                                type="button"
                                onClick={() => setTab("video")}
                            >
                                视频
                            </button>
                            <button
                                className={tab === "image" ? "is-active" : ""}
                                type="button"
                                onClick={() => setTab("image")}
                            >
                                图片
                            </button>
                        </div>
                        {isImageTab && imageUrls.length > 1 ? (
                            <span className="trade-order-snapshot-phone__counter">
                                {currentImageIndex + 1}/{imageUrls.length}
                            </span>
                        ) : null}
                    </div>

                    <div className="trade-order-snapshot-phone__body">
                        {isGroupBuying ? (
                            <div className="trade-order-snapshot-phone__group-message">
                                <div className="trade-order-snapshot-phone__group-price">
                                    <div className="trade-order-snapshot-phone__group-price-left">
                                        <div className="trade-order-snapshot-phone__group-current-price">
                                            <span>￥</span>
                                            <strong>{formatCurrency(product?.groupUnitPrice ?? 0)}</strong>
                                            <span>/吨</span>
                                        </div>
                                        <div className="trade-order-snapshot-phone__group-original-price">
                                            <span>原价</span>
                                            <span>￥{formatCurrency(product?.unitPrice ?? 0)}/吨</span>
                                            <span>即将恢复原价</span>
                                        </div>
                                    </div>
                                    <div className="trade-order-snapshot-phone__group-price-right">
                                        <strong>价格立减</strong>
                                        <span>￥{formatCurrency((product?.unitPrice ?? 0) - (product?.groupUnitPrice ?? 0))}/吨</span>
                                    </div>
                                </div>
                                <div className="trade-order-snapshot-phone__group-other-message">
                                    <h3>{product?.title || "-"}</h3>
                                    {product?.productLabel?.length ? (
                                        <div className="trade-order-snapshot-phone__labels">
                                            {product.productLabel.map(label => (
                                                <span key={label}>{label}</span>
                                            ))}
                                        </div>
                                    ) : null}
                                    <p>{getEnterpriseName(product?.supplierEnterprise) || "--"}</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="trade-order-snapshot-phone__price-row">
                                    <strong>¥{fmtMoney(product?.unitPrice ?? order?.unitPrice ?? 0)}</strong>
                                    <span>元/吨</span>
                                    <em>含税（{product?.taxRate ?? 3}%）</em>
                                </div>
                                <h3>{product?.title || "-"}</h3>
                                {product?.productLabel?.length ? (
                                    <div className="trade-order-snapshot-phone__labels">
                                        {product.productLabel.map(label => (
                                            <span key={label}>{label}</span>
                                        ))}
                                    </div>
                                ) : null}
                                <p>
                                    起订吨数 {product?.minOrderTon ?? "-"} 吨 | 库存量 {product?.inventoryQuantity ?? "-"} 吨
                                </p>
                                <p className="trade-order-snapshot-phone__description">
                                    {getEnterpriseName(product?.supplierEnterprise) || "--"}
                                </p>
                            </>
                        )}

                        <div className={`trade-order-snapshot-phone__grid${isGroupBuying ? " is-group-buying" : ""}`}>
                            <span>品类</span>
                            <strong>{product?.productTypeName || "--"}</strong>
                            <span>规格</span>
                            <strong>{product?.productTypeSpec || "--"}</strong>
                            <span className={isGroupBuying ? "is-wide-label" : ""}>供应商</span>
                            <strong className={isGroupBuying ? "is-wide-value" : ""}>
                                {getEnterpriseName(product?.supplierEnterprise) || "--"}
                            </strong>
                            <span className={isGroupBuying ? "is-wide-label" : ""}>提货地址</span>
                            <strong className={isGroupBuying ? "is-wide-value" : ""}>{deliveryAddress}</strong>
                        </div>

                        {product?.parameterInfo?.length ? (
                            <section className="trade-order-snapshot-phone__section">
                                <h4>商品参数</h4>
                                <div className="trade-order-snapshot-phone__parameter-table">
                                    {product.parameterInfo.map(item => (
                                        <div key={`${item.parameterName}-${item.parameterValue}`}>
                                            <span>{item.parameterName}</span>
                                            <strong>{item.parameterValue}</strong>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        <section className="trade-order-snapshot-phone__section">
                            <h4>商品介绍</h4>
                            <div dangerouslySetInnerHTML={{ __html: product?.introduce || "--" }} />
                        </section>
                        <section className="trade-order-snapshot-phone__section">
                            <h4>提货信息</h4>
                            <p>{product?.deliveryDescn || "--"}</p>
                        </section>
                        <section className="trade-order-snapshot-phone__section">
                            <h4>{productRuleTitle}</h4>
                            <p>{product?.settlementDescn || "--"}</p>
                        </section>
                    </div>
                </div>
                <img alt="" className="trade-order-snapshot-phone__border" src={getPublicFileUrl("admin/iPhone-border.png")} />
            </div>
        </div>
    );
}
